
import { Options, Vue } from "vue-class-component";
import PrivilegesService from "../../service/PrivilegesService.js";
import Toaster from "../../helpers/Toaster";
import { useStore, ActionTypes } from "../../store";
import { RoleLevels } from "../../constants/RoleLevels";

interface IRoles {
    type: any;
    type_id: any;
    id: number;
    name: string;
    level_id?: number;
}

@Options({
    title: "Privileges",
    components: {},
})
export default class Privileges extends Vue {
    // Make RoleLevels available in template
    private RoleLevels = RoleLevels;
    
    private selectedRoleID = 0;
    private toast;
    private privilegesService;
    private store = useStore();
    private home = { icon: "pi pi-home", to: "/" };
    private items = [
        { label: "Initialization", to: "/home/initialization" },
        { label: "Privileges", to: "privileges" },
    ];

    private roles: IRoles[] = [];
    private permission: IRoles[] = [];
    private checkedPermission = [];
    private rolePharmacyIds: number[] = [];
    private editablePharmacyIds: number[] = [];
    private currentPharmacyId: any = null;
    private createRoleDialog = false;
    private submitted = false;
    private pharmacyList: any[] = [];
    private newRole: { name: string; level_id: number | null; pharmacy_ids: number[] } = {
        name: '',
        level_id: null,
        pharmacy_ids: []
    };
    private allLevelOptions = [
        { label: 'Level 1 - Admin (Highest)', value: 1 },
        { label: 'Level 10 - Account Manager', value: 10 },
        { label: 'Level 20 - Pharmacy Admin', value: 20 },
        { label: 'Level 30 - Branch Admin/Cashier/Reporting (Lowest)', value: 30 },
    ];
    
    // Computed property to filter level options based on current user's role
    get levelOptions() {
        const currentLevelId = this.getRole()?.level_id || 999;
        // Admin (level 1) can create any role, others can only create roles with higher level_id
        if (currentLevelId === 1) {
            return this.allLevelOptions;
        }
        return this.allLevelOptions.filter(option => option.value > currentLevelId);
    }
    private permissionTypes = [
        { id: 1, name: "Dashboard" },
        { id: 2, name: "Products" },
        { id: 3, name: "POS/Sales" },
        { id: 4, name: "Purchase" },
        { id: 5, name: "Payment Methods" },
        { id: 6, name: "Settings" },
        { id: 7, name: "Reports" },
        { id: 8, name: "Pharmacy/Branches" },
    ];

    //DEFAULT METHOD OF TYPE SCRIPT
    created() {
        this.privilegesService = new PrivilegesService();
        this.toast = new Toaster();
    }

    //CALLNING AFTER CONSTRUCTOR GET CALLED
    mounted() {
        this.loadList();
    }

    //FETCH THE DATA FROM SERVER
    loadList() {
        this.privilegesService.getItems(this.selectedRoleID).then((data) => {
            this.currentPharmacyId = data.currentPharmacyId;
            this.pharmacyList = data.pharmacyList || [];
            this.rolePharmacyIds = Array.isArray(data.rolePharmacyIds) ? data.rolePharmacyIds : [];
            this.editablePharmacyIds = Array.isArray(this.rolePharmacyIds) ? [...this.rolePharmacyIds] : [];
            console.log('Role Pharmacy IDs loaded:', this.rolePharmacyIds);
            this.roles = data.roles.filter(
                (role) => (role.level_id > this.getRole().level_id || this.getRole().level_id == RoleLevels.SUPER_ADMIN)
            );
            this.permission = data.permission;
            this.checkedPermission = data.assignedPermission;
        });
    }

    getPharmacyName(pharmacyId: number): string {
        const pharmacy = this.pharmacyList.find(p => p.id === pharmacyId);
        return pharmacy ? pharmacy.name : 'Unknown Pharmacy';
    }

    openCreateRoleDialog() {
        this.newRole = {
            name: '',
            level_id: RoleLevels.BRANCH, // Default to Branch level
            pharmacy_ids: this.currentPharmacyId ? [this.currentPharmacyId] : [] // Default to current pharmacy
        };
        this.submitted = false;
        this.createRoleDialog = true;
    }

    // Get role display name based on level_id
    getRoleDisplayName(levelId: number): string {
        switch(levelId) {
            case RoleLevels.SUPER_ADMIN:
                return 'Admin';
            case RoleLevels.INTER_PHARMACY:
                return 'Account Manager';
            case RoleLevels.PHARMACY:
                return 'Pharmacy Admin';
            case RoleLevels.BRANCH:
                return 'Branch Staff';
            default:
                return 'Unknown';
        }
    }

    hideCreateRoleDialog() {
        this.createRoleDialog = false;
        this.submitted = false;
    }

    createRole() {
        this.submitted = true;
        
        // Validate required fields
        if (!this.newRole.name || !this.newRole.level_id) {
            return;
        }
        
        // If multiple pharmacies available, at least one pharmacy is required
        if (this.pharmacyList.length > 1 && (!this.newRole.pharmacy_ids || this.newRole.pharmacy_ids.length === 0)) {
            return;
        }

        // Use current pharmacy if only one available, otherwise use selected pharmacies
        const pharmacyIds = this.pharmacyList.length > 1 ? this.newRole.pharmacy_ids : [this.currentPharmacyId];

        console.log('Creating role with pharmacy IDs:', pharmacyIds);
        this.privilegesService
            .createRole(this.newRole.name, this.newRole.level_id, pharmacyIds)
            .then((res) => {
                console.log('Create role response:', res);
                if (res.alert === 'success') {
                    this.toast.showSuccess(res.msg);
                    this.hideCreateRoleDialog();
                    this.loadList(); // Reload the roles list
                } else {
                    this.toast.showError(res.msg);
                }
            })
            .catch((error) => {
                console.error('Create role error:', error);
                this.toast.showError('Failed to create role');
            });
    }

    savePermissions() {
        // First save pharmacy assignments if they've changed
        if (this.hasPharmacyChanges) {
            this.privilegesService
                .updateRolePharmacies(this.selectedRoleID, this.editablePharmacyIds)
                .then((res) => {
                    if (res.alert === 'success') {
                        this.rolePharmacyIds = [...this.editablePharmacyIds];
                        // Then save permissions
                        this.savePermissionsOnly();
                    } else {
                        this.toast.showError(res.msg);
                    }
                })
                .catch((error) => {
                    console.error('Update role pharmacies error:', error);
                    this.toast.showError('Failed to update pharmacy assignments');
                });
        } else {
            // Just save permissions if no pharmacy changes
            this.savePermissionsOnly();
        }
    }

    savePermissionsOnly() {
        this.privilegesService
            .saveItem(this.selectedRoleID, this.checkedPermission)
            .then((res) => {
                this.toast.handleResponse(res);
            });
    }

    get hasPharmacyChanges(): boolean {
        if (!Array.isArray(this.editablePharmacyIds) || !Array.isArray(this.rolePharmacyIds)) {
            return false;
        }
        if (this.editablePharmacyIds.length !== this.rolePharmacyIds.length) {
            return true;
        }
        const sorted1 = [...this.editablePharmacyIds].sort();
        const sorted2 = [...this.rolePharmacyIds].sort();
        return !sorted1.every((val, idx) => val === sorted2[idx]);
    }

    get groupedPermissions() {
        return this.permission.reduce((groups, item) => {
            const typeId = item.type_id; // Assuming `type_id` exists in the permission object
            item.type = this.permissionTypes.find(
                (pName) => pName.id == typeId
            )?.name; // Add the type to the item
            if (!groups[typeId]) {
                groups[typeId] = {
                    type: item.type,
                    items: [],
                };
            }
            groups[typeId].items.push(item);

            console.log(item);
            return groups;
        }, {});
    }

    getRole() {
        const store = useStore();
        const roleString = store.getters.getUserRole;
        // Check if it's already parsed or needs parsing
        if (typeof roleString === 'string') {
            try {
                return JSON.parse(roleString);
            } catch (e) {
                // If parsing fails, return a default role object
                return { level_id: 999 };
            }
        }
        // If it's already an object, return it
        return roleString || { level_id: 999 };
    }

}
